import apiClient from './apiClient';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'capped_out'
  | 'capped_out_session_grace';

export interface MyEntitlements {
  scribe: boolean;
  diarization: boolean;
  premiumVoices: boolean;
  whatsappBot: boolean;
  telegramBot: boolean;
  customDomain: boolean;
  byok: boolean;
  caps: {
    scribeMinutes: number | 'unlimited';
    whatsappMessages: number | 'unlimited';
    telegramMessages: number | 'unlimited';
    seats: number;
    /** LLM token allowance (seat-scaled), per month + per day. */
    tokensPerMonth: number | 'unlimited';
    tokensPerDay: number | 'unlimited';
  };
  usage: {
    scribeMinutes: number;
    scribeMinutesOverage: number;
    whatsappMessages: number;
    telegramMessages: number;
    period: string;
    /** Tokens consumed in the current month / day windows. */
    tokensMonth: number;
    tokensDay: number;
  };
  seatsPurchased: number;
  /** Highest active-member count the tenant can reach by inviting. While
   *  paid plans are gated, this is the tier's seatsCeiling so a tenant on
   *  seatsPurchased=1 isn't permanently locked at one seat. */
  inviteCap: number;
  /** Hard upper bound for seats on the current tier (resolve-time clamp). */
  seatsCeiling: number;
  /** Plan's purchasable AI-seat cap — the real "up to N on this plan" to show
   *  (distinct from seatsCeiling). e.g. Pro = 3. */
  checkoutSeatCeiling: number;
  /** Plan's purchasable staff-seat cap. */
  checkoutStaffCeiling: number;
  /** Extra non-AI staff seats purchased (Receptionist/Billing). */
  staffSeatsPurchased: number;
  /** Cap on active non-clinical members (purchased staff seats + free
   *  allowance on a paid plan; tier ceiling during trial / coming-soon). */
  staffInviteCap: number;
  /** Plan's purchasable records-only clinician cap. */
  checkoutClinicalBasicCeiling?: number;
  /** Records-only clinician seats purchased — full clinical privileges,
   *  no AI. Billed from seat one (no free allowance). */
  clinicalBasicSeatsPurchased?: number;
  /** Cap on active records-only clinicians. */
  clinicalBasicInviteCap?: number;
  /**
   * Whether the caps above are per-seat or one flat clinic-wide budget. The
   * numbers are ALREADY resolved through this — it's here so usage meters can
   * be labelled correctly ("/ seat / month" vs "for the clinic").
   */
  capBasis?: { scribeMinutes: 'per_seat' | 'clinic'; tokens: 'per_seat' | 'clinic' };
  /**
   * Set when MAIE sponsors this clinic — there is nothing to pay and no
   * self-serve billing. Hide checkout/upgrade surfaces when present.
   */
  comped?: { reason: string; since: string } | null;
  activeMembers: number;
  subscriptionStatus: SubscriptionStatus;
  overrunHandling: 'auto_overcharge' | 'require_action';
  /** True iff the OWNER explicitly picked overrunHandling; false on system default. */
  overrunHandlingExplicit: boolean;
  /** Owner-initiated upgrade request awaiting MAIE review, if any. */
  upgradeRequest: {
    toTier: string;
    requestedAt: string;
    note: string | null;
    /** Detail captured by the request modal (older requests omit these). */
    seats?: number | null;
    staffSeats?: number | null;
    timing?: string | null;
    phone?: string | null;
  } | null;
  trialEndsAt: string | null;
  /** True while the 30-day free trial window is still open. After expiry the
   *  tenant is plain FREE (manual EMR, AI off). */
  trialActive: boolean;
  isFreeTier: boolean;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const unwrap = <T>(payload: ApiEnvelope<T> | T): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in (payload as object) &&
    'data' in (payload as object)
  ) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
};

export const entitlementsApi = {
  me: async (): Promise<MyEntitlements> => {
    const { data } = await apiClient.get<ApiEnvelope<MyEntitlements>>(
      '/auth/me/entitlements',
    );
    return unwrap(data);
  },

  /**
   * Owner-writable preference for paid tiers; routed through the
   * system-settings bulk upsert since `PUT /:key` requires the row to
   * already exist. billing_preferences.* keys are deliberately
   * owner-visible/writable (unlike the platform-only billing.* keys).
   */
  setOverrunHandling: async (
    handling: 'auto_overcharge' | 'require_action',
  ): Promise<void> => {
    await apiClient.put('/system-settings/bulk', {
      settings: {
        'billing_preferences.overrun_handling': handling,
      },
    });
  },

  /**
   * Records an upgrade intent. The actual tier flip happens via
   * platform-admin once MAIE staff reviews the request. Idempotent:
   * re-requesting overwrites the prior intent.
   */
  requestUpgrade: async (
    toTier: 'pro' | 'clinic' | 'enterprise',
    note?: string,
  ): Promise<{ success: true; toTier: string; requestedAt: string }> => {
    const { data } = await apiClient.post<{
      success: true;
      toTier: string;
      requestedAt: string;
    }>('/auth/me/billing/upgrade-request', { toTier, note });
    return data;
  },
};
